import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  createBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '10px 20px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer',
  },
  yearLabel: {
    fontSize: '13px', fontWeight: '700', color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '1px',
    marginBottom: '8px', marginTop: '24px',
  },
  groupHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 16px', backgroundColor: '#fff',
    borderRadius: '8px', border: '1px solid #e5e7eb',
    cursor: 'pointer', marginBottom: '8px', userSelect: 'none',
  },
  groupHeaderOpen: { backgroundColor: '#EBF5FF', border: '1px solid #bfdbfe' },
  groupTitle: { fontSize: '14px', fontWeight: '700', color: '#111827', flex: 1 },
  groupCount: {
    fontSize: '11px', fontWeight: '700', color: '#0052CC',
    backgroundColor: '#dbeafe', padding: '2px 8px', borderRadius: '20px',
  },
  groupChevron: { fontSize: '12px', color: '#6b7280' },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px', marginBottom: '8px',
  },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  cardTitle: { fontSize: '16px', fontWeight: '700', color: '#111827' },
  cardEnv: {
    fontSize: '11px', fontWeight: '600', color: '#0052CC',
    backgroundColor: '#EBF5FF', padding: '2px 8px', borderRadius: '20px',
  },
  cardContext: {
    fontSize: '13px', color: '#374151', marginBottom: '12px',
    backgroundColor: '#f9fafb', borderRadius: '6px', padding: '8px 12px',
    borderLeft: '3px solid #0052CC',
    display: '-webkit-box', WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  cardField: { fontSize: '13px', color: '#6b7280', marginBottom: '6px' },
  cardFieldVal: { fontWeight: '600', color: '#374151' },
  cardActions: {
    display: 'flex', gap: '8px', marginTop: '16px',
    paddingTop: '16px', borderTop: '1px solid #f3f4f6',
  },
  runBtn: {
    flex: 1, padding: '8px', backgroundColor: '#0052CC', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  editBtn: {
    padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  deleteBtn: {
    padding: '8px 16px', backgroundColor: '#fef2f2', color: '#dc2626',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  empty: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '64px 32px',
    textAlign: 'center', border: '1px solid #e5e7eb',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: '#6b7280', marginBottom: '24px' },
  loading: { textAlign: 'center', padding: '64px', color: '#6b7280' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px',
  },
};

function groupByMonthYear(items) {
  const groups = {};
  items.forEach(item => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${month}`;
    if (!groups[key]) groups[key] = { year, month, items: [] };
    groups[key].items.push(item);
  });
  return Object.values(groups).sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month - a.month
  );
}

export default function ScenarioLibrary() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchScenarios(); }, []);

  async function fetchScenarios() {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/scenarios?owner_id=${currentUser.uid}`);
      const data = await res.json();
      const scenarios = Array.isArray(data) ? data : [];
      setScenarios(scenarios);

      if (scenarios.length > 0) {
        const groups = groupByMonthYear(scenarios);
        if (groups.length > 0) {
          const first = groups[0];
          setOpenGroups({ [`${first.year}-${first.month}`]: true });
        }
      }
    } catch {
      setError('Failed to load scenarios.');
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this scenario?')) return;
    try {
      await fetch(`${API}/api/scenarios/${id}`, { method: 'DELETE' });
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch {
      setError('Failed to delete scenario.');
    }
  }

  function toggleGroup(key) {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const groups = groupByMonthYear(scenarios);
  const byYear = {};
  groups.forEach(g => {
    if (!byYear[g.year]) byYear[g.year] = [];
    byYear[g.year].push(g);
  });
  const sortedYears = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Scenario Library</div>
          <div style={styles.sub}>
            {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''} · grouped by date
          </div>
        </div>
        <button style={styles.createBtn} onClick={() => navigate('/scenarios/new')}>
          + New Scenario
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <div style={styles.loading}>Loading scenarios...</div>
      ) : scenarios.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>◉</div>
          <div style={styles.emptyTitle}>No scenarios yet</div>
          <div style={styles.emptyText}>Create your first simulation scenario to get started.</div>
          <button style={styles.createBtn} onClick={() => navigate('/scenarios/new')}>
            + New Scenario
          </button>
        </div>
      ) : (
        sortedYears.map(year => (
          <div key={year}>
            <div style={styles.yearLabel}>{year}</div>
            {byYear[year].map(group => {
              const key = `${group.year}-${group.month}`;
              const isOpen = !!openGroups[key];
              return (
                <div key={key}>
                  <div
                    style={{ ...styles.groupHeader, ...(isOpen ? styles.groupHeaderOpen : {}) }}
                    onClick={() => toggleGroup(key)}
                  >
                    <div style={styles.groupTitle}>{MONTHS[group.month]}</div>
                    <div style={styles.groupCount}>
                      {group.items.length} scenario{group.items.length !== 1 ? 's' : ''}
                    </div>
                    <div style={styles.groupChevron}>{isOpen ? '▲' : '▼'}</div>
                  </div>
                  {isOpen && (
                    <div style={styles.grid}>
                      {group.items.map(scenario => (
                        <div key={scenario.id} style={styles.card}>
                          <div style={styles.cardHeader}>
                            <div style={styles.cardTitle}>{scenario.title}</div>
                            {scenario.environment && (
                              <div style={styles.cardEnv}>{scenario.environment}</div>
                            )}
                          </div>
                          {scenario.context && (
                            <div style={styles.cardContext}>{scenario.context}</div>
                          )}
                          <div style={styles.cardField}>
                            Provider: <span style={styles.cardFieldVal}>{scenario.llm_provider}</span>
                          </div>
                          <div style={styles.cardField}>
                            Model: <span style={styles.cardFieldVal}>{scenario.llm_model}</span>
                          </div>
                          <div style={styles.cardField}>
                            Language: <span style={styles.cardFieldVal}>
                              {scenario.language || 'English'}
                            </span>
                          </div>
                          <div style={styles.cardField}>
                            Max Turns: <span style={styles.cardFieldVal}>{scenario.max_turns}</span>
                          </div>
                          <div style={styles.cardActions}>
                            <button
                              style={styles.runBtn}
                              onClick={() => {
                                // Unlock audio context on user gesture before navigating
                                const silent = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//OEAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAAYbhBZOpAAAAAAAAAAAAAAAAAAAA//OEZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCenp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6enp6e//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAAnEhBZOpAAAAAAAAAAAAAAAAAAAA');
                                silent.play()
                                  .then(() => navigate(`/simulations/new?scenario=${scenario.id}`))
                                  .catch(() => navigate(`/simulations/new?scenario=${scenario.id}`));
                              }}
                            >
                              ▶ Run
                            </button>
                            <button style={styles.editBtn} onClick={() => navigate(`/scenarios/${scenario.id}`)}>
                              Edit
                            </button>
                            <button style={styles.deleteBtn} onClick={() => handleDelete(scenario.id)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </AppLayout>
  );
}
